import { Outlet, NavLink, Navigate, Link } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import {
    LayoutDashboard,
    Store as StoreIcon,
    Package,
    ShoppingBag,
    Sparkles,
    LogOut,
    ArrowUpRight,
    Loader2,
    ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BannerStrip from "@/components/shared/BannerStrip";
import { toast } from "sonner";

const NAV = [
    { to: "/dashboard", label: "Resumen", icon: LayoutDashboard, end: true },
    { to: "/dashboard/onboarding", label: "Onboarding", icon: Sparkles },
    { to: "/dashboard/store", label: "Mi tienda", icon: StoreIcon },
    { to: "/dashboard/products", label: "Productos", icon: Package },
    { to: "/dashboard/orders", label: "Pedidos", icon: ShoppingBag },
];

export default function MerchantLayout() {
    const { user, isAuthenticated, isLoading, logout, becomeMerchant } = useAuth();
    const [promoting, setPromoting] = useState(false);

    if (isLoading) return null;
    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: "/dashboard" }} replace />;
    }
    if (user.role === "admin") return <Navigate to="/admin" replace />;

    // Customer landed on /dashboard — invite them to upgrade.
    if (user.role === "customer") {
        const handlePromote = async () => {
            setPromoting(true);
            try {
                await becomeMerchant();
                toast.success("¡Bienvenido vendedor!", {
                    description: "Tu cuenta ya está lista para crear tu tienda.",
                });
            } catch {
                toast.error("No se pudo activar tu cuenta de vendedor");
            } finally {
                setPromoting(false);
            }
        };
        return (
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                <div className="rounded-2xl border border-zinc-200 bg-gradient-to-br from-primary-subtle via-white to-amber-50 p-6 sm:p-10 text-center">
                    <div className="mx-auto w-12 h-12 rounded-xl bg-primary text-primary-foreground grid place-items-center">
                        <StoreIcon className="w-6 h-6" aria-hidden="true" />
                    </div>
                    <h1 className="mt-5 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
                        ¿Quieres vender en Tiendify?
                    </h1>
                    <p className="mt-3 text-sm text-zinc-600 max-w-md mx-auto leading-relaxed">
                        Tu cuenta actual es de comprador. Activa el modo
                        vendedor para crear tu tienda en línea, subir productos
                        y empezar a vender sin renta mensual.
                    </p>
                    <Button
                        onClick={handlePromote}
                        disabled={promoting}
                        size="lg"
                        className="mt-6 bg-primary hover:bg-primary-hover text-primary-foreground h-12 px-6"
                    >
                        {promoting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                        ) : null}
                        Activar mi cuenta de vendedor
                        {!promoting && <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />}
                    </Button>
                    <p className="mt-4 text-xs text-zinc-500">
                        Sin renta mensual. Cancela cuando quieras.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <>
        <BannerStrip scope="dashboard" />
        <div
            data-testid="merchant-layout"
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10 overflow-x-hidden"
        >
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
                <aside className="lg:col-span-3 min-w-0">
                    <div className="lg:sticky lg:top-24 space-y-4">
                        {/* Profile card — desktop only (info ya está en navbar dropdown en móvil) */}
                        <div className="hidden lg:block rounded-xl border border-zinc-200 bg-white p-4">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                                Panel vendedor
                            </p>
                            <p className="mt-2 text-sm font-semibold text-zinc-900 truncate">
                                {user.name}
                            </p>
                            <p className="text-xs text-zinc-500 truncate">
                                {user.email}
                            </p>
                        </div>

                        <div className="relative">
                            {/* Fade derecho que indica scroll horizontal en mobile */}
                            <div
                                aria-hidden="true"
                                className="lg:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white via-white/80 to-transparent z-10"
                            />
                            <nav
                                aria-label="Panel del vendedor"
                                className="flex lg:flex-col gap-1 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0"
                            >
                                {NAV.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end={item.end}
                                        data-testid={`merchant-nav-${item.to.split("/").pop() || "root"}`}
                                        className={({ isActive }) =>
                                            `flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 ${
                                                isActive
                                                    ? "bg-primary-subtle text-primary-hover"
                                                    : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
                                            }`
                                        }
                                    >
                                        <item.icon className="w-4 h-4" aria-hidden="true" />
                                        {item.label}
                                    </NavLink>
                                ))}
                                <Link
                                    to="/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-50 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                                >
                                    <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                                    Ver tienda pública
                                </Link>
                                {/* Logout solo desktop — en mobile está en el dropdown del Navbar. */}
                                <button
                                    onClick={logout}
                                    data-testid="merchant-logout"
                                    className="hidden lg:flex items-center gap-2 px-3 h-11 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40"
                                >
                                    <LogOut className="w-4 h-4" aria-hidden="true" />
                                    Cerrar sesión
                                </button>
                            </nav>
                        </div>
                    </div>
                </aside>

                <main className="lg:col-span-9 min-w-0">
                    <Outlet />
                </main>
            </div>
        </div>
        </>
    );
}
